<template>
  <section class="relative py-10 overflow-hidden">
    <!-- Background Pattern -->
    <div class="absolute inset-0 bg-grid-slate-100 bg-[size:20px_20px] [mask-image:radial-gradient(white,transparent_70%)]" />

    <!-- Floating Elements -->
    <div class="absolute top-10 left-10 w-32 h-32 bg-blue-400/10 rounded-full blur-2xl" />
    <div class="absolute bottom-10 right-10 w-24 h-24 bg-indigo-400/10 rounded-full blur-xl" />
    <div class="absolute top-1/2 left-1/4 w-2 h-2 bg-blue-400 rounded-full animate-ping" />
    <div class="absolute top-1/3 right-1/3 w-1 h-1 bg-indigo-400 rounded-full animate-pulse" />

    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Header -->
      <div
        v-if="showTitle || showSubTitle"
        class="text-center mb-16"
      >
        <div class="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-semibold border border-blue-200 mb-6">
          <svg
            class="w-4 h-4 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
              clip-rule="evenodd"
            />
          </svg>
          <span v-text="t('Subscription Plans')" />
        </div>
        <h2
          v-if="showTitle"
          class="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
          v-text="t(title)"
        />
        <slot>
          <p
            v-if="showSubTitle"
            class="text-xl text-gray-600 max-w-2xl mx-auto"
            v-text="t('Select the plan that works best for you and enjoy exclusive benefits!')"
          />
        </slot>
      </div>

      <!-- Modern Pricing Cards -->
      <div 
        class="grid gap-8 lg:gap-10 mx-auto"
        :class="gridClasses"
      >
        <PlanCard
          v-for="(plan, index) in subscriptionPlans"
          :key="index"
          :plan="plan"
          :has-discount="plan.has_discount || false"
          :discounted-price="plan.discounted_price || 0"
        />
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PlanCard from '@/Components/Subscription/PlanCard.vue';

const { t } = useI18n();

const props = withDefaults(defineProps<{
  title?: string;
  subtitle?: string;
  showTitle?: boolean;
  showSubTitle?: boolean;
  subscriptionPlans: Array<{
    id: number;
    name: string;
    stripe_price: string,
    is_recommended: boolean,
    price_formatted: string;
    price_formatted_with_symbol: string;
    order: number;
    features: {
      label: string;
      value: string;
    }[];
    has_discount?: boolean;
    discounted_price?: number;
  }>
}>(), {
  showTitle: true,
  showSubTitle: true,
  title: 'Subscribe & Get Started',
  subtitle: 'Select the plan that works best for you and enjoy exclusive benefits!',
});

// Dynamic grid classes based on the number of plans.
const gridClasses = computed(() => {
  const planCount = props.subscriptionPlans.length;
  
  if (planCount === 1) {
    return 'max-w-md mx-auto';
  } else if (planCount === 2) {
    return 'lg:grid-cols-2 max-w-4xl';
  } else if (planCount === 3) {
    return 'lg:grid-cols-3 max-w-6xl';
  } else if (planCount >= 4) {
    return 'lg:grid-cols-2 xl:grid-cols-4 max-w-7xl';
  }
  
  return 'max-w-5xl';
});
</script>
